/**
 * AI Core Worker API 基礎 URL
 * 2026-09-09（SEC R1/R8）：改行 same-origin——gateway /api/core/* → AI_CORE binding，
 * dashboard 唔再裸連跨域 workers.dev（CORS 面收窄）；pages.dev 直開時先 fallback 絕對 URL。
 */
const API_BASE = location.hostname.endsWith("pages.dev")
  ? "https://wtsaibot.com/api/core"
  : "/api/core";

/**
 * WhatsApp Connector Worker API 基礎 URL（e-Shop／推薦計劃管理 API）
 * 2026-09-08：改行 same-origin（gateway /api/referrals/* ＋ /api/eshop/* → wa binding）——
 * 跨域 workers.dev 會被 adblock／CORS preflight 擋（Failed to fetch 實錄）；
 * 直接開 pages.dev 時先 fallback 返絕對 URL。
 */
const WA_API_BASE = location.hostname.endsWith("pages.dev")
  ? "https://aibot-wa.jkdcoding.com"
  : "";

function getAdminKey(): string {
  return localStorage.getItem("admin_key") || "";
}

/**
 * 401 自動登出（2026-09-17）：key 輪換後舊 stored key 令全部 API 401，
 * 而 App.tsx 只檢查「有冇 key」唔驗證有效——後台會永遠卡喺 Unauthorized。
 * 修法：有 key 但收到 401＝key 已失效→清除＋reload 踢返登入頁（輸入新 key 即復原）。
 */
/**
 * 401 處理（v2，2026-10-27）：key 失效＝清除＋事件通知 App 即時切登入頁。
 * 原版（2026-09-17）用 location.reload()——配合 Layout 每 30 秒輪詢，舊金鑰用戶會
 * 陷入「載入中→401→整頁重載→再載入」無限閃載（Captain 實錄：內容永遠空白＋重複登入登出）。
 * 事件方案零重載：App 收 admin-unauthorized 即 setIsLoggedIn(false)，Layout 卸載＝輪詢終止。
 */
function handle401(): Response {
  if (getAdminKey()) {
    localStorage.removeItem("admin_key");
    try {
      window.dispatchEvent(new CustomEvent("admin-unauthorized"));
    } catch { /* 舊環境無 CustomEvent＝照舊只清 key */ }
  }
  return new Response(JSON.stringify({ error: "Unauthorized——管理員密鑰已失效，請重新輸入" }), {
    status: 401,
    headers: { "Content-Type": "application/json; charset=utf-8" },
  });
}

/**
 * 20 秒超時（2026-10-27，Captain 實錄「後台空白一片」）：apiFetch 原本無超時——
 * 手機網絡下 fetch 吊死＝頁面永遠停在「載入中…」單行（外殼在、內容空）。
 * 超時後 fetch reject → 頁面 error 分支接管（有重試掣），空白死局不再。
 */
const FETCH_TIMEOUT_MS = 20_000;

export async function apiFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = `${API_BASE}${path}`;
  const headers = new Headers(options.headers || {});
  headers.set("Content-Type", "application/json");

  const key = getAdminKey();
  if (key) {
    headers.set("x-admin-key", key);
  }

  const res = await fetch(url, {
    ...options,
    headers,
    signal: options.signal ?? AbortSignal.timeout(FETCH_TIMEOUT_MS),
  });
  if (res.status === 401 && key) {
    return handle401();
  }
  return res;
}

/** Connector（aibot-wa）端請求——同一 admin key（x-admin-key），e-Shop 站點管理用 */
async function waFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = `${WA_API_BASE}${path}`;
  const headers = new Headers(options.headers || {});
  headers.set("Content-Type", "application/json");

  const key = getAdminKey();
  if (key) {
    headers.set("x-admin-key", key);
  }

  const res = await fetch(url, { ...options, headers });
  if (res.status === 401 && key) {
    return handle401();
  }
  return res;
}

/** Referral v2（2026-09-19）：同 waFetch 但 export 俾 ReferralV2Section 用 */
export async function waFetchV2(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = `${WA_API_BASE}${path}`;
  const headers = new Headers(options.headers || {});
  headers.set("Content-Type", "application/json");
  const key = getAdminKey();
  if (key) headers.set("x-admin-key", key);
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401 && key) return handle401();
  return res;
}

/** 多品牌 Commerce API 一律經主 gateway，不能誤送舊 connector origin。 */
export async function commerceFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const base = location.hostname.endsWith("pages.dev") ? "https://wtsaibot.com" : "";
  const headers = new Headers(options.headers || {});
  headers.set("Content-Type", "application/json");
  const key = getAdminKey();
  if (key) headers.set("x-admin-key", key);
  const res = await fetch(`${base}${path}`, { ...options, headers, signal: options.signal ?? AbortSignal.timeout(FETCH_TIMEOUT_MS) });
  if (res.status === 401 && key) return handle401();
  return res;
}

// ── 知識庫管理 ──
export interface IngestKnowledgeResult {
  success: boolean;
  ingested: number;
  tenantId: string;
  facadeId?: string;
}

export async function ingestKnowledge(
  tenantId: string,
  content: string,
  facadeId?: string,
): Promise<IngestKnowledgeResult> {
  const res = await apiFetch("/admin/ingest", {
    method: "POST",
    body: JSON.stringify({ tenantId, content, ...(facadeId ? { facadeId } : {}) }),
  });
  return parseAdminResponse<IngestKnowledgeResult>(res);
}

// ── 租戶管理 ──
export async function getTracking(days: number) {
  const res = await apiFetch(`/admin/tracking?days=${days}`);
  return res.json();
}

export async function getMonthlyUsage(tenantId?: string) {
  const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const res = await apiFetch(`/admin/monthly-usage${q}`);
  return res.json();
}

export async function listTenants() {
  const res = await apiFetch("/admin/tenants");
  return res.json();
}

export async function createTenant(
  payload: Record<string, string | number | null>
) {
  const res = await apiFetch("/admin/tenants", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function getLLMKeysStatus() {
  const res = await apiFetch("/admin/llm-keys-status");
  return res.json();
}

export async function updateTenant(
  tenantId: string,
  updates: Record<string, string | number>
) {
  const res = await apiFetch(`/admin/tenants/${tenantId}`, {
    method: "POST",
    body: JSON.stringify({ tenantId, ...updates }),
  });
  return res.json();
}

// ── 轉真人控制台（Handoff Console）──
export async function generateConsoleLink(tenantId: string) {
  const res = await apiFetch(`/admin/tenants/${tenantId}/console-link`, {
    method: "POST",
  });
  return res.json();
}

export async function generateShortLink(tenantId: string) {
  const res = await apiFetch(`/admin/tenants/${tenantId}/short-link`, {
    method: "POST",
  });
  return res.json();
}

// ── 額度管理 ──
export async function getQuota(tenantId: string): Promise<number> {
  const res = await apiFetch(`/admin/tenants`);
  const data = await res.json();
  const tenant = data.tenants?.find((t: any) => t.tenant_id === tenantId);
  return tenant?.trial_quota ?? 0;
}

export async function setQuota(tenantId: string, quota: number) {
  const res = await apiFetch("/internal/init-quota", {
    method: "POST",
    body: JSON.stringify({ tenantId, quota }),
  });
  return res.json();
}

// ── 對話日誌 ──
export async function listChatLogs(
  tenantId?: string,
  sessionId?: string,
  limit = 50
) {
  const params = new URLSearchParams();
  if (tenantId) params.set("tenantId", tenantId);
  if (sessionId) params.set("sessionId", sessionId);
  params.set("limit", String(limit));
  const res = await apiFetch(`/admin/chat-logs?${params.toString()}`);
  return res.json();
}

// ── 對話 session（2026-09-21 照門戶模式重造：清單＋展開）──
export async function listChatSessions(tenantId?: string, days = 30) {
  const params = new URLSearchParams();
  if (tenantId) params.set("tenantId", tenantId);
  params.set("days", String(days));
  const res = await apiFetch(`/admin/chat-sessions?${params.toString()}`);
  return res.json();
}

export async function expandChatSession(tenantId: string, sessionId: string) {
  const params = new URLSearchParams();
  params.set("tenantId", tenantId);
  params.set("session", sessionId);
  const res = await apiFetch(`/admin/chat-sessions?${params.toString()}`);
  return res.json();
}

// ── 試用漏斗平台開關（2026-09-23）──
export async function getTrialFunnelStatus() {
  const res = await apiFetch(`/admin/trial/enabled`);
  return res.json();
}

export async function setTrialFunnelEnabled(enabled: boolean) {
  const res = await apiFetch(`/admin/trial/enabled`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  });
  return res.json();
}

// ── 功能模塊 ──
export async function listTenantFeatures(tenantId: string) {
  const res = await apiFetch(`/admin/tenants/${tenantId}/features`);
  return res.json();
}

export async function updateTenantFeature(
  tenantId: string,
  featureId: string,
  updates: {
    enabled?: boolean;
    quota?: number | null;
    config?: Record<string, unknown> | null;
    expires_at?: number | null;
  }
) {
  const res = await apiFetch(`/admin/tenants/${tenantId}/features/${featureId}`, {
    method: "POST",
    body: JSON.stringify(updates),
  });
  return res.json();
}

export async function deleteTenant(tenantId: string, opts?: { releaseChannel?: boolean }) {
  const res = await apiFetch(`/admin/tenants/${tenantId}`, {
    method: "DELETE",
    // 明示勾選先帶 body（Captain 2026-10-23 方案 A）：release_channel:true＝
    // 同時釋放 CloudWAPI 通道；唔勾＝唔帶 body（後端預設唔釋放）
    ...(opts?.releaseChannel ? { body: JSON.stringify({ release_channel: true }) } : {}),
  });
  return res.json();
}

// ── 租戶通道實況（2026-10-28 三系統協調）：按需查詢（唔好頁面載入就 N 個上游呼叫）──
export type TenantChannelState = "connected" | "disconnected" | "not_found" | "not_configured" | "unknown";
export async function getTenantChannelStatus(tenantId: string): Promise<{
  success: boolean;
  state: TenantChannelState;
  channel?: string;
  status?: string;
  error?: string;
}> {
  const res = await apiFetch(`/admin/tenant-channel-status?t=${encodeURIComponent(tenantId)}`);
  return res.json();
}

// ── 反濫用配置 ──
export async function getAntiAbuseConfig() {
  const res = await apiFetch("/admin/anti-abuse-config");
  return res.json();
}

export async function updateAntiAbuseConfig(payload: {
  lite?: number;
  pro?: number;
  resetMin?: number;
  burstLite?: number;
  burstPro?: number;
  burstWindowMin?: number;
  burstEnabled?: boolean;
}) {
  const res = await apiFetch("/admin/anti-abuse-config", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function getTenantAntiAbuseConfig(tenantId: string) {
  const res = await apiFetch(`/admin/tenants/${tenantId}/anti-abuse-config`);
  return res.json();
}

// ── 知識庫學習迴圈 ──
export async function listKbSuggestions(
  tenantId?: string,
  status?: string,
  limit = 50
) {
  const params = new URLSearchParams();
  if (tenantId) params.set("tenantId", tenantId);
  if (status) params.set("status", status);
  params.set("limit", String(limit));
  const res = await apiFetch(`/admin/kb-changelog?${params.toString()}`);
  return res.json();
}

export async function approveKbSuggestion(
  id: number,
  updates?: { suggested_question?: string; suggested_content?: string }
) {
  const res = await apiFetch(`/admin/kb-changelog/${id}/approve`, {
    method: "POST",
    body: JSON.stringify(updates || {}),
  });
  return res.json();
}

export async function rejectKbSuggestion(id: number) {
  const res = await apiFetch(`/admin/kb-changelog/${id}/reject`, {
    method: "POST",
  });
  return res.json();
}

export async function ingestKnowledgeAppend(tenantId: string, content: string) {
  const res = await apiFetch("/admin/ingest-append", {
    method: "POST",
    body: JSON.stringify({ tenantId, content }),
  });
  return res.json();
}

// ── e-Shop 站點管理（connector /api/eshop/admin/*，Go-Live M9）──

export interface EshopSiteView {
  id: number;
  site_slug: string;
  device_phone: string;
  status: "active" | "disabled";
  rate_limit_per_min: number;
  created_at: number;
  updated_at: number;
  send24h: number;
  failed24h: number;
}

export async function listEshopSites(): Promise<EshopSiteView[]> {
  const res = await waFetch("/api/eshop/admin/sites");
  const data = await res.json();
  return data.sites ?? [];
}

/** 建立；回傳 { site, token }——token 明文只此一次 */
export async function createEshopSite(
  siteSlug: string,
  devicePhone: string,
  rateLimitPerMin: number
): Promise<{ site?: EshopSiteView; token?: string; error?: string; notice?: string }> {
  const res = await waFetch("/api/eshop/admin/sites", {
    method: "POST",
    body: JSON.stringify({ site_slug: siteSlug, device_phone: devicePhone, rate_limit_per_min: rateLimitPerMin }),
  });
  return res.json();
}

/** 動作：enable|disable|rotate|rate-limit|canary */
export async function eshopSiteAction(
  id: number,
  action: string,
  extra: Record<string, unknown> = {}
): Promise<{ success?: boolean; token?: string; detail?: string; error?: string; notice?: string }> {
  const res = await waFetch(`/api/eshop/admin/sites/${id}`, {
    method: "POST",
    body: JSON.stringify({ action, ...extra }),
  });
  return res.json();
}

// ── 增值線索（Value Ladder Phase 2）：師傅訪談挖到嘅商機線索看板 ──

export interface LeadView {
  id: number;
  tenant_id: string;
  session_id: string;
  signal_type: string;
  trigger_quote: string | null;
  content: string;
  status: "new" | "viewed";
  created_at: number;
  updated_at: number;
}

export async function listLeads(tenantId?: string, status?: string): Promise<LeadView[]> {
  const params = new URLSearchParams();
  if (tenantId) params.set("tenant_id", tenantId);
  if (status) params.set("status", status);
  const res = await apiFetch(`/admin/leads?${params.toString()}`);
  const data = await res.json();
  return data.leads ?? [];
}

/** Layout 側欄閃光點用：new 狀態線索數（未點入看過就長期顯示） */
export async function countNewLeads(): Promise<number> {
  try {
    const leads = await listLeads(undefined, "new");
    return leads.length;
  } catch {
    return 0;
  }
}

export async function markLeadsViewed(ids: number[]) {
  const res = await apiFetch("/admin/leads", {
    method: "PATCH",
    body: JSON.stringify({ ids, status: "viewed" }),
  });
  return res.json();
}

// ── 📣 推薦計劃（任務 I，經銷商模式——connector /api/referrals/admin/*）──

export interface ReferrerView {
  id: number;
  name: string;
  phone: string;
  code: string;
  reward_mode: "percentage" | "fixed";
  reward_value: number;
  note: string | null;
  active: number;
  created_at: number;
  visits: number;
  leads: number;
  converted: number;
  rewards_total: number;
  parent_id?: number | null;
  parent_name?: string | null;
  sub_pct?: number | null;
  subs?: number;
  status?: string | null;
  is_org?: number;
}

/** Phase 1（G-02）：逐單拆帳明細——讀 onboarding_orders.split_snapshot（只讀） */
export interface ReferralSplitView {
  order_id: string;
  customer: string | null;
  plan: string | null;
  amount: number | null;
  status: string | null;
  ref_code: string | null;
  partner_id: string | null;
  created_at: number | null;
  referrer_id: number | null;
  referrer_name: string | null;
  parent_id: number | null;
  parent_name: string | null;
  split: {
    gross?: number; agent_pct?: number; agent_hkd?: number;
    sub_pct?: number; sub_hkd?: number; direct_pct?: number; direct_hkd?: number;
    org_pct?: number; org_hkd?: number; aibot_hkd?: number;
  } | null;
  unfrozen: boolean;
}

export interface ReferralLeadView {
  id: number;
  code: string;
  referrer_id: number;
  referrer_name: string;
  phone: string;
  status: "lead" | "converted";
  first_message: string | null;
  deal_amount: number | null;
  reward_amount: number | null;
  created_at: number;
  converted_at: number | null;
}

export async function getReferralOverview(): Promise<{ referrers: ReferrerView[]; leads: ReferralLeadView[] }> {
  const res = await waFetch("/api/referrals/admin/overview");
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json();
}

export async function getReferralSplits(partnerId?: string): Promise<ReferralSplitView[]> {
  const qs = partnerId ? `?partner_id=${encodeURIComponent(partnerId)}` : "";
  const res = await waFetch(`/api/referrals/admin/v2/splits${qs}`);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  const data = (await res.json()) as { ok: boolean; splits: ReferralSplitView[] };
  return data.splits || [];
}

/** P14（2026-10-15）：平台 admin 兩級治理入口——後端與夥伴後台共用 govTeam* 交易函式，
 *  事件軌 actor_role='admin'、比例審計 actor='admin:platform'。 */
export type GovAction = "team-suspend" | "team-remove" | "team-reassign" | "team-sub-pct";
export async function adminGovTeam(action: GovAction, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const res = await waFetch(`/api/referrals/admin/v2/${action}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error(String(data.error || `HTTP ${res.status}`));
  return data;
}

export async function createReferrer(b: {
  name: string; phone: string; code: string; reward_mode: string; reward_value: number; note?: string;
}) {
  const res = await waFetch("/api/referrals/admin/referrers", {
    method: "POST",
    body: JSON.stringify(b),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data as { ok: true; link: string };
}

export async function updateReferrer(
  id: number,
  b: Partial<{ name: string; phone: string; reward_mode: string; reward_value: number; note: string; active: number; sub_pct: number | null }>
) {
  const res = await waFetch(`/api/referrals/admin/referrers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(b),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return true;
}

export async function deleteReferrer(id: number) {
  const res = await waFetch(`/api/referrals/admin/referrers/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return true;
}

export async function convertReferralLead(leadId: number, dealAmount: number) {
  const res = await waFetch("/api/referrals/admin/convert", {
    method: "POST",
    body: JSON.stringify({ lead_id: leadId, deal_amount: dealAmount }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data as { ok: true; reward_amount: number; notified: string };
}

// ── 📈 營運透視（2026-09-08：每租戶經營數據＋成本毛利——頂層視角）──
export interface OpsTenantRow {
  tenant_id: string;
  bot_name: string;
  plan_tier: string;
  fee_hkd: number;
  msgs30d: number;
  user_msgs30d: number;
  ai_replies30d: number;
  handoffs30d: number;
  guests30d: number;
  msgs7d: number;
  trend: "up" | "down" | "flat" | "new" | "idle";
  trend_pct: number | null;
  est_cost_hkd: number;
  billed_pct?: number;
  margin_hkd: number;
  last_active: number;
}

export async function getOpsOverview() {
  const res = await apiFetch("/admin/ops-overview");
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json() as Promise<{
    success: boolean;
    generatedAt: number;
    tenants: OpsTenantRow[];
    totals: { msgs30d: number; msgs7d: number; guests30d: number; cost_hkd: number; fee_hkd: number; growing: number; shrinking: number };
  }>;
}

// ── 平台配置（platform_config）：品牌署名等平台級設定 ──
export async function getPlatformConfig(key: string): Promise<{ exists: boolean; value?: string }> {
  const res = await apiFetch(`/admin/platform-config?key=${encodeURIComponent(key)}`);
  return res.json() as Promise<{ exists: boolean; value?: string }>;
}

export async function setPlatformConfig(key: string, value: string): Promise<{ success: boolean }> {
  const res = await apiFetch("/admin/platform-config", {
    method: "POST",
    body: JSON.stringify({ key, value }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json() as Promise<{ success: boolean }>;
}

// ── 商戶門戶密碼重置（經 ai-core 代理 portal admin-reset；PORTAL_ADMIN_KEY 留伺服器端）──
// sendToMerchant=true：ai-core 經中央監控號直接發登入資料去租戶登記手機（失敗照返 password，前端跌剪貼板）
export async function resetPortalPassword(
  tenantId: string,
  sendToMerchant = false
): Promise<{
  ok: boolean;
  tenantId: string;
  password: string;
  sent?: boolean;
  sentTo?: string;
  sendError?: string;
}> {
  const res = await apiFetch("/admin/portal-reset", {
    method: "POST",
    body: JSON.stringify(sendToMerchant ? { tenantId, sendToMerchant: true } : { tenantId }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json() as Promise<{ ok: boolean; tenantId: string; password: string; sent?: boolean; sentTo?: string; sendError?: string }>;
}

// ── 系統事件日誌（v27：cron／出站發送／LLM 硬失敗統一查閱）──
export interface SystemLogRow {
  id: number;
  ts: number;
  level: string;
  source: string;
  event: string;
  tenant_id: string | null;
  detail: string | null;
}

export async function listSystemLogs(
  params: { level?: string; source?: string; tenantId?: string; q?: string; limit?: number } = {}
): Promise<{ logs: SystemLogRow[] }> {
  const qs = new URLSearchParams();
  if (params.level) qs.set("level", params.level);
  if (params.source) qs.set("source", params.source);
  if (params.tenantId) qs.set("tenantId", params.tenantId);
  if (params.q) qs.set("q", params.q);
  qs.set("limit", String(params.limit ?? 100));
  const res = await apiFetch(`/admin/system-logs?${qs.toString()}`);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json() as Promise<{ logs: SystemLogRow[] }>;
}

/* ═══ 開通待辦（線 1 onboard 主線，2026-09-11）═══ */

export interface OnboardOrder {
  order_id: string;
  customer_name: string | null;
  customer_phone: string;
  plan: string;
  lang: string;
  tenant_id: string | null;
  status: string; // awaiting_provision → provisioned → welcome_sent → active
  paid_at: number;
  api_key_set_at: number | null;
  welcome_sent_at: number | null;
  source: string | null;
  onboard_status: string | null;
  kb_chunk_count: number | null;
  bound: number;
  bot_tests: number;
  billing_cycle?: string | null;
  managed_order_no?: string | null;
  transaction_no?: string | null;
  channel_id?: string | null;
  channel_username?: string | null;
  channel_state?: string | null;
  channel_attempts?: number | null;
  channel_last_error?: string | null;
  channel_lease_until?: number | null;
  partner_id?: string | null;
  partner_name?: string | null;
  ref_code?: string | null;
  amount?: number | null;
}

export type OnboardAutoProvisionStatus = "ready" | "provisioned" | "queued" | "manual_required" | "skipped";

export interface OnboardAutoProvisionResult {
  ok?: boolean;
  status?: OnboardAutoProvisionStatus | "error";
  state?: OnboardAutoProvisionStatus | "error";
  retryable?: boolean;
  error?: string;
  message?: string;
  orderId?: string;
  tenantId?: string | null;
  orderStatus?: string;
  channelId?: string | null;
  action?: string;
  channel_state?: string | null;
  channelState?: string | null;
}

export async function onboardAutoProvision(orderId: string): Promise<OnboardAutoProvisionResult> {
  const res = await apiFetch("/api/onboard/auto-provision", {
    method: "POST",
    body: JSON.stringify({ orderId }),
  });
  const data = (await res.json().catch(() => ({}))) as OnboardAutoProvisionResult;
  const outcome = data.status ?? data.state;
  if (!res.ok && !data.retryable && outcome !== "queued" && outcome !== "manual_required") {
    throw new Error(data.error || data.message || `HTTP ${res.status}`);
  }
  return data;
}

export async function getOnboardingOrders(): Promise<OnboardOrder[]> {
  const res = await apiFetch("/admin/onboard-orders");
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  const data = await res.json();
  return data.orders ?? [];
}

/** Layout 側欄 badge 用：awaiting_provision 待辦數（30 秒輪詢） */
export async function countPendingOnboard(): Promise<number> {
  try {
    const res = await apiFetch("/admin/onboard-count");
    if (!res.ok) return 0;
    const data = await res.json();
    return data.pending ?? 0;
  } catch {
    return 0;
  }
}

export async function onboardManualRegister(body: {
  customerName: string;
  customerPhone: string;
  plan: string;
  lang: string;
}): Promise<{ orderId: string; tenantId: string | null }> {
  const res = await apiFetch("/api/onboard/manual", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json();
}

export async function onboardProvision(orderId: string, apiKey: string): Promise<void> {
  const res = await apiFetch("/api/onboard/provision", {
    method: "POST",
    body: JSON.stringify({ orderId, apiKey }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
}

export async function onboardWelcomeSend(orderId: string): Promise<void> {
  const res = await apiFetch("/api/onboard/welcome-send", {
    method: "POST",
    body: JSON.stringify({ orderId }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
}

export async function onboardWelcomeCopy(orderId: string): Promise<string> {
  const res = await apiFetch("/api/onboard/welcome-copy", {
    method: "POST",
    body: JSON.stringify({ orderId }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  const data = await res.json();
  if (!data.welcomeText) throw new Error("回應缺 welcomeText");
  return data.welcomeText as string;
}

/** 跟進包小項 1：作廢超時／問題單（active 拒取消；租戶資料保留） */
export async function onboardCancel(orderId: string): Promise<void> {
  const res = await apiFetch("/api/onboard/cancel", {
    method: "POST",
    body: JSON.stringify({ orderId }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
}

/* ═══ 🤝 戰略夥伴（P1，2026-09-13；與🎁推薦計劃完全獨立 D7）═══ */

export interface PartnerListResult {
  success: boolean;
  partners: import("../types").Partner[];
  featureDefs: import("../types").PartnerFeatureDef[];
}

export async function listPartners(): Promise<PartnerListResult> {
  const res = await apiFetch("/admin/partners");
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json();
}

/** 建立；apiKey 明文只此一次返回 */
export async function createPartner(payload: {
  partner_id: string;
  name: string;
  website?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  wa_line?: string | null;
  wholesale_pricing?: Record<string, number> | null;
  billing_note?: string | null;
  co_branding?: Record<string, string> | null;
  default_features?: string[];
}): Promise<{ partnerId: string; apiKey: string }> {
  const res = await apiFetch("/admin/partners", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export async function updatePartner(
  partnerId: string,
  updates: Partial<{
    name: string;
    website: string | null;
    contact_name: string | null;
    contact_phone: string | null;
    wa_line: string | null;
    wholesale_pricing: Record<string, number> | null;
    billing_note: string | null;
    co_branding: Record<string, string> | null;
    default_features: string[];
    status: string;
  }>
): Promise<void> {
  const res = await apiFetch(`/admin/partners/${encodeURIComponent(partnerId)}`, {
    method: "POST",
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
}

/** 名下有租戶會 409（先解除掛鉤先刪到） */
export async function deletePartner(partnerId: string): Promise<void> {
  const res = await apiFetch(`/admin/partners/${encodeURIComponent(partnerId)}`, { method: "DELETE" });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
}

/** 重新生成 API key（舊 key 即刻作廢）；明文只此一次返回 */
export async function rotatePartnerKey(partnerId: string): Promise<{ apiKey: string }> {
  const res = await apiFetch(`/admin/partners/${encodeURIComponent(partnerId)}/rotate-key`, {
    method: "POST",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// v56→2026-10-28：夥伴登入連結永久制——發送仍可用；主要交付＝後台 copy（getPartnerLink）
export async function sendPartnerLogin(partnerId: string): Promise<{ success: boolean; sent: Array<{ phone: string; link: string }>; count: number }> {
  const res = await apiFetch(`/admin/partners/${encodeURIComponent(partnerId)}/send-login`, {
    method: "POST",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Superadmin 版面：列夥伴旗下全部 Agent（2026-10-30）——含掛靠與小代理數
export interface PartnerAgentView {
  code: string;
  name: string | null;
  phone: string;
  status: string | null;
  activated: boolean;
  parentName: string | null;
  subs: number;
  clicks: number;
  converted: number;
}
export async function listPartnerAgents(partnerId: string): Promise<{ success: boolean; agents: PartnerAgentView[] }> {
  const res = await apiFetch(`/admin/partners/${encodeURIComponent(partnerId)}/agents`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// 2026-10-28 永久連結制（Captain 拍板）：後台取現行連結（冇有就簽）／重設（作廢全部＋簽新）
export async function getPartnerLink(partnerId: string): Promise<{ success: boolean; link: string; phone?: string }> {
  const res = await apiFetch(`/admin/partners/${encodeURIComponent(partnerId)}/link`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Superadmin 代入夥伴後台（2026-10-30）：不經夥伴 WhatsApp——直接取一次性登入 URL（24h）
// code 可選：留空＝Master 視角；指定旗下 Agent code＝該 Agent 視角
export async function partnerLoginAs(
  partnerId: string,
  code?: string
): Promise<{ success: boolean; url: string; view: { code: string; name: string | null; isOrg: boolean }; expiresInH: number }> {
  const res = await apiFetch(`/admin/partners/${encodeURIComponent(partnerId)}/login-as`, {
    method: "POST",
    body: JSON.stringify(code ? { code } : {}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export async function resetPartnerLink(partnerId: string): Promise<{ success: boolean; link: string; rotated: boolean }> {
  const res = await apiFetch(`/admin/partners/${encodeURIComponent(partnerId)}/link/reset`, {
    method: "POST",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── 員工席位（Phase C，2026-10-10：HK$80/席/月 KPay 直收無試用期；admin 界面先行）──
export interface SeatUserView {
  id: number;
  username: string;
  display_name: string | null;
  role: string;
  status: string;
  created_at: number;
}
export interface SeatOrderView {
  order_id: string;
  seats: number;
  amount_hkd: number;
  status: string;
  managed_order_no: string | null;
  transaction_no: string | null;
  paid_at: number | null;
  created_at: number;
}
export interface SeatsView {
  ok: boolean;
  error?: string;
  planTier?: string;
  includedSeats?: number;
  activeOperators?: number;
  paidSeats?: number;
  billableSeats?: number;
  users?: SeatUserView[];
  orders?: SeatOrderView[];
}

export async function listSeats(tenantId: string): Promise<SeatsView> {
  const res = await apiFetch(`/admin/tenants/${encodeURIComponent(tenantId)}/seats`);
  return res.json();
}

export async function createSeatUser(tenantId: string, username: string, displayName: string) {
  const res = await apiFetch(`/admin/tenants/${encodeURIComponent(tenantId)}/seats`, {
    method: "POST",
    body: JSON.stringify({ username, displayName }),
  });
  return res.json();
}

export async function seatAction(
  tenantId: string,
  action: "reset" | "status" | "delete" | "order",
  body: { id?: number; status?: string; seats?: number }
) {
  const res = await apiFetch(`/admin/tenants/${encodeURIComponent(tenantId)}/seats/${action}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
}

/** 對話品質台側欄（2026-10-11）：租戶資料＋轉真人次數＋❌答錯數（唯讀聚合） */
export async function getChatQuality(tenantId: string, sessionId?: string) {
  const params = new URLSearchParams();
  params.set("tenantId", tenantId);
  if (sessionId) params.set("session", sessionId);
  const res = await apiFetch(`/admin/chat-quality?${params.toString()}`);
  return res.json();
}

// ── Employee Pack 與主動發送 ──
export interface EmployeeFacade {
  id: string;
  kbNamespace: string;
  workflow: string;
  policy: string;
  skills: string[];
  description?: string;
  enabled?: boolean;
}

export interface EmployeePack {
  packId: string;
  version: string;
  role: string;
  visibility: string;
  defaultFacade: string;
  executionMode: string;
  facades: EmployeeFacade[];
}

export interface EmployeePackTenant {
  tenantId: string;
  packId: string | null;
  execution_mode?: string;
  executionMode?: string;
  facadeKbEnabled: boolean;
  updatedAt: number | null;
}

export interface EmployeeFacadeProfile {
  tenantId: string;
  facadeId: string;
  chunkCount: number;
  ready: boolean;
  hasSystemPrompt: boolean;
  updatedAt: number | null;
}

export interface EmployeePackList {
  packs: EmployeePack[];
  tenants?: EmployeePackTenant[];
  tenantModes?: Record<string, string>;
  facadeProfiles?: EmployeeFacadeProfile[];
}

export interface OutboundAllowlistEntry {
  contact_id: string;
  name: string;
  phone: string;
  approved_by: string;
  approved_at: string | number;
  expires_at: string | number | null;
  note: string | null;
  tenant_id?: string;
}

export interface OutboundDraft {
  id: string;
  to_phone: string;
  body: string;
  body_hash: string;
  status: string;
  template_id: string | null;
  template_version: string | null;
}

export interface OutboundMessage {
  id: string;
  tenant_id?: string;
  contact_id?: string;
  to_phone?: string;
  body?: string;
  body_hash?: string;
  status: string;
  template_id?: string | null;
  template_version?: string | null;
  provider_ref?: string | null;
  error?: string | null;
  requested_by?: string;
  approved_by?: string;
  idempotency_key?: string;
  created_at?: string | number;
  updated_at?: string | number;
}

export interface OutboundPrepareInput {
  tenantId: string;
  contactId: string;
  templateId?: string;
  templateVersion?: string;
  body: string;
  idempotencyKey: string;
  requestedBy: string;
}

export interface OutboundConfirmResult {
  id: string;
  status: string;
  provider_ref: string | null;
  error: string | null;
}

function adminErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (typeof error !== "object" || error === null) return "";
  const { code, message } = error as { code?: unknown; message?: unknown };
  const codeText = typeof code === "string" ? code : "";
  const messageText = typeof message === "string" ? message : "";
  if (codeText && messageText) return `${codeText}: ${messageText}`;
  return messageText || codeText;
}

async function parseAdminResponse<T>(res: Response): Promise<T> {
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = typeof data === "object" && data !== null
      ? (data as { error?: unknown }).error
      : undefined;
    throw new Error(adminErrorMessage(error) || `HTTP ${res.status}`);
  }
  return data as T;
}

export async function listEmployeePacks(tenantId?: string): Promise<EmployeePackList> {
  const query = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : "";
  const res = await apiFetch(`/admin/employee-packs${query}`);
  return parseAdminResponse<EmployeePackList>(res);
}

export interface GoogleCalendarStatus {
  success?: boolean;
  tenantId: string;
  connected: boolean;
  needsReauth: boolean;
  googleEmail: string | null;
  connectedAt: number | null;
  connectUrl: string | null;
  error?: string;
}

export async function getGoogleCalendarStatus(tenantId: string, next: string): Promise<GoogleCalendarStatus> {
  const res = await apiFetch(`/admin/tenants/${encodeURIComponent(tenantId)}/google-calendar?next=${encodeURIComponent(next)}`);
  const data = await parseAdminResponse<GoogleCalendarStatus>(res);
  if (data.success === false) throw new Error(data.error || "Google Calendar 狀態載入失敗");
  if (data.tenantId !== tenantId) throw new Error("Google Calendar 回應的租戶不符，已停止操作");
  return data;
}

export async function listOutboundAllowlist(): Promise<{ entries: OutboundAllowlistEntry[] }> {
  const res = await apiFetch("/admin/outbound/allowlist");
  return parseAdminResponse<{ entries: OutboundAllowlistEntry[] }>(res);
}

export async function addOutboundAllowlist(payload: {
  tenantId: string;
  contactId: string;
  approvedBy: string;
  note?: string;
}): Promise<{ success?: boolean; entry?: OutboundAllowlistEntry; error?: string }> {
  const res = await apiFetch("/admin/outbound/allowlist", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const data = await parseAdminResponse<{ success?: boolean; entry?: OutboundAllowlistEntry; error?: string }>(res);
  if (data.success === false) throw new Error(data.error || "加入 allowlist 失敗");
  return data;
}

export async function prepareOutbound(
  payload: OutboundPrepareInput
): Promise<{ draft: OutboundDraft; error?: string }> {
  const res = await apiFetch("/admin/outbound/prepare", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const data = await parseAdminResponse<{ draft: OutboundDraft; error?: string }>(res);
  if (!data.draft) throw new Error(data.error || "回應缺 draft");
  return data;
}

export async function confirmOutbound(payload: {
  draftId: string;
  bodyHash: string;
  approvedBy: string;
}): Promise<OutboundConfirmResult> {
  const res = await apiFetch("/admin/outbound/confirm", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return parseAdminResponse<OutboundConfirmResult>(res);
}

export async function listOutboundMessages(tenantId: string): Promise<{ messages: OutboundMessage[] }> {
  const res = await apiFetch(`/admin/outbound/messages?tenantId=${encodeURIComponent(tenantId)}`);
  return parseAdminResponse<{ messages: OutboundMessage[] }>(res);
}
